/* eslint-disable max-len */
import { FC, useEffect, useMemo, useState } from 'react';
import { demeterApi } from '../../../../Apis/Apis';
import { UnitOfMeasure } from '../../../../Generated/Raven';
import { Currency, DemeterFilterTimeSpan, ListMarketPricesWithConversionsResponse, MarketPricesTimeSpan } from '../../../../Generated/Raven-Demeter';
import useMultipleApis from '../../../Apis/Hooks/useMultipleApisHook';
import Carousel from '../../../Components/Carousel/Carousel';
import { IChartBarDataSeries, IChartDataSeries } from '../../../Components/Charts/ChartDefinitions';
import useLanguage from '../../../Services/Language/useLanguageHook';
import styles from './CalendarSpreadCalculator.module.scss';
import CalendarSpreadCalculatorContextChart from './CalendarSpreadCalculatorContextChart';
import CalendarSpreadCalculatorSeasonalChart from './CalendarSpreadCalculatorSeasonalChart';
import calendarSpreadCalculatorService from './CalendarSpreadCalculatorService';
import { ICalendarSpreadCalculationResult, ICalendarSpreadSelections } from './CalendarSpreadDefinitions';

interface ICalendarSpreadCalculatorChartCarouselProps {
    calendarSpreadCalculatorSelections: ICalendarSpreadSelections;
    currency: Currency;
    unitOfMeasure: UnitOfMeasure;
    onCalculationCalculated?: (calculationResult: ICalendarSpreadCalculationResult) => void;
}

interface IlistMarketPricesWithConversionsRequest {
    instrumentCode: string;
    timeSpan: DemeterFilterTimeSpan;
    currency: Currency;
    unitOfMeasure: UnitOfMeasure;
}

const displayDecimalPlacesMinimum = 4;
// Ensure that enough data is available to overlap contract 1 selection.
// Example: contract 1 = Aug 25, contract 2 = Jan 26
const lookBackBuffer = 1;
const previousContractYearsToLookBack = 3;
const previousYearContract = 1;

const CalendarSpreadCalculatorChartCarousel: FC<ICalendarSpreadCalculatorChartCarouselProps> = (props: ICalendarSpreadCalculatorChartCarouselProps) => {
    const [translations] = useLanguage();
    const [dataSourceTag, setDataSourceTag] = useState<string[]>([]);
    const [seasonalLineSeries, setSeasonalLineSeries] = useState<IChartDataSeries[]>();
    const [projectionLineSeries, setProjectionLineSeries] = useState<IChartDataSeries[]>();
    const [projectionBarSeries, setProjectionBarSeries] = useState<IChartBarDataSeries>({ label: translations.words.spread, data: [], format: 'currency' });
    const [selectedTimeSpan, setSelectedTimeSpan] = useState<DemeterFilterTimeSpan>(DemeterFilterTimeSpan.SixMonths);

    const [isLoadinglistMarketPricesWithConversions, refreshlistMarketPricesWithConversions, listMarketPricesWithConversionsResponses] = useMultipleApis<
        IlistMarketPricesWithConversionsRequest,
        ListMarketPricesWithConversionsResponse
    >(
        (requests) => {
            if (!requests || requests.length === 0) {
                return null;
            }

            return requests.map((request) =>
                demeterApi.listMarketPricesWithConversions(
                    request.instrumentCode,
                    request.timeSpan as MarketPricesTimeSpan,
                    request.currency,
                    request.unitOfMeasure,
                ),
            );
        },
        { stopAutoExecute: true },
    );

    useEffect(() => {
        if (!props.calendarSpreadCalculatorSelections.contract1 || !props.calendarSpreadCalculatorSelections.contract2) {
            return;
        }

        const { contract1 } = props.calendarSpreadCalculatorSelections;
        const { contract2 } = props.calendarSpreadCalculatorSelections;
        const expiredContracts1 = props.calendarSpreadCalculatorSelections.symbol1.expiredSymbolContracts.filter(
            (x) => x.month === contract1.month && x.year >= contract1.year - (previousContractYearsToLookBack + lookBackBuffer),
        );
        const expiredContracts2 = props.calendarSpreadCalculatorSelections.symbol2.expiredSymbolContracts.filter(
            (x) => x.month === contract2.month && x.year >= contract2.year - (previousContractYearsToLookBack + lookBackBuffer),
        );

        const requests1 = [contract1, ...expiredContracts1].map((contract) => ({
            instrumentCode: contract.reutersInstrumentCode,
            timeSpan: DemeterFilterTimeSpan.TwoYears,
            currency: props.currency,
            unitOfMeasure: props.unitOfMeasure,
        }));
        const requests2 = [contract2, ...expiredContracts2].map((contract) => ({
            instrumentCode: contract.reutersInstrumentCode,
            timeSpan: DemeterFilterTimeSpan.TwoYears,
            currency: props.currency,
            unitOfMeasure: props.unitOfMeasure,
        }));

        refreshlistMarketPricesWithConversions([...requests1, ...requests2]);
    }, [props.calendarSpreadCalculatorSelections]);

    const handleTimeSpanChange = (timeSpan: DemeterFilterTimeSpan) => {
        setSelectedTimeSpan(timeSpan);
    };

    useEffect(() => {
        if (!listMarketPricesWithConversionsResponses?.length || isLoadinglistMarketPricesWithConversions) {
            return;
        }

        const projectionLabel1 = `${props.calendarSpreadCalculatorSelections.contract1.monthYearName} ${props.calendarSpreadCalculatorSelections.contract1.reutersInstrumentCode}`;
        const projectionLabel2 = `${props.calendarSpreadCalculatorSelections.contract2.monthYearName} ${props.calendarSpreadCalculatorSelections.contract2.reutersInstrumentCode}`;
        const averageLabel = `${previousContractYearsToLookBack} ${translations.words.year} ${translations.words.average}`;
        const { contract1, contract2 } = props.calendarSpreadCalculatorSelections;
        const lastYearContract1 = props.calendarSpreadCalculatorSelections.symbol1.expiredSymbolContracts.find(
            (x) => x.month === contract1.month && x.year === contract1.year - previousYearContract,
        );
        const lastYearContract2 = props.calendarSpreadCalculatorSelections.symbol2.expiredSymbolContracts.find(
            (x) => x.month === contract2.month && x.year === contract2.year - previousYearContract,
        );

        if (!lastYearContract1 || !lastYearContract2) {
            return;
        }

        const previousYearsContracts1 = props.calendarSpreadCalculatorSelections.symbol1.expiredSymbolContracts
            .filter((x) => x.month === contract1.month && x.year >= contract1.year - previousContractYearsToLookBack)
            .sort((a, b) => b.year - a.year);

        const previousYearsContracts2 = props.calendarSpreadCalculatorSelections.symbol2.expiredSymbolContracts
            .filter((x) => x.month === contract2.month && x.year >= contract2.year - previousContractYearsToLookBack)
            .sort((a, b) => b.year - a.year);

        const thisYearAndLastYearSeaonalLineSeries = calendarSpreadCalculatorService.getthisYearAndLastYearSeaonalLineSeries(
            listMarketPricesWithConversionsResponses,
            contract1,
            contract2,
            lastYearContract1,
            lastYearContract2,
            previousYearsContracts1,
            previousYearsContracts2,
            translations.text.thisYear,
            translations.text.lastYear,
            averageLabel,
        );

        const projectionDataWithLineAndBarSeries = calendarSpreadCalculatorService.getProjectionLineAndBarSeries(
            listMarketPricesWithConversionsResponses,
            contract1,
            contract2,
            selectedTimeSpan,
            projectionLabel1,
            projectionLabel2,
            translations.words.spread,
        );

        if (props.onCalculationCalculated) {
            const calculationResult = calendarSpreadCalculatorService.getCalculationGridData(listMarketPricesWithConversionsResponses, contract1, contract2);
            props.onCalculationCalculated(calculationResult);
        }

        setDataSourceTag(listMarketPricesWithConversionsResponses.map((response) => response.dataSourceTag ?? ''));
        setProjectionLineSeries([...projectionDataWithLineAndBarSeries.lineSeries]);
        setProjectionBarSeries({ ...projectionDataWithLineAndBarSeries.barSeries });
        setSeasonalLineSeries(thisYearAndLastYearSeaonalLineSeries);
    }, [listMarketPricesWithConversionsResponses, isLoadinglistMarketPricesWithConversions, selectedTimeSpan, props.calendarSpreadCalculatorSelections]);

    const isLoading = useMemo(() => {
        if (isLoadinglistMarketPricesWithConversions) {
            return true;
        }

        if (!props.calendarSpreadCalculatorSelections || !projectionLineSeries || !projectionBarSeries || !seasonalLineSeries) {
            return true;
        }

        return false;
    }, [isLoadinglistMarketPricesWithConversions, props.calendarSpreadCalculatorSelections, projectionLineSeries, projectionBarSeries, seasonalLineSeries]);

    const title = useMemo(
        () =>
            `${props.calendarSpreadCalculatorSelections.symbol1.displayName} 
            ${props.calendarSpreadCalculatorSelections.contract1.monthYearName} 
            (${props.calendarSpreadCalculatorSelections.contract1.reutersInstrumentCode})
            ${translations.words.vs} 
            ${props.calendarSpreadCalculatorSelections.symbol2.displayName} 
            ${props.calendarSpreadCalculatorSelections.contract2.monthYearName} 
            (${props.calendarSpreadCalculatorSelections.contract2.reutersInstrumentCode})`,
        [props.calendarSpreadCalculatorSelections],
    );

    return (
        <div className={styles.calendar_spread_calculator_chart_carousel}>
            <Carousel track={{ eventCategory: 'CalendarSpreadCalculator', eventAction: 'CarouselChartChanged' }}>
                <CalendarSpreadCalculatorContextChart
                    // eslint-disable-next-line prettier/prettier
                    title={title}
                    dataSourceTag={dataSourceTag}
                    linesSeries={projectionLineSeries ?? []}
                    barSeries={projectionBarSeries}
                    currency={props.currency}
                    unitOfMeasure={props.unitOfMeasure}
                    displayDecimalPlacesMinimum={displayDecimalPlacesMinimum}
                    isLoading={isLoading || !projectionLineSeries?.length || !projectionBarSeries}
                    handleTimeSpanChange={handleTimeSpanChange}
                />
                <CalendarSpreadCalculatorSeasonalChart
                    title={`${title} ${translations.words.spread}`}
                    dataSourceTag={dataSourceTag}
                    linesSeries={seasonalLineSeries ?? []}
                    currency={props.currency}
                    unitOfMeasure={props.unitOfMeasure}
                    isLoading={isLoading || !seasonalLineSeries?.length}
                />
            </Carousel>
        </div>
    );
};

export default CalendarSpreadCalculatorChartCarousel;
